import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, useTheme } from '@mui/material';

import { StatusChip } from '@components/StatusChip';
import { CELL_STATE_COLORS } from '../../constants';
import { formatPositionLabel } from '../../utils';

import type { RackCell } from '../../types';

type Props = {
  flatCells: RackCell[];
  onCellClick: (cell: RackCell) => void;
};

const MobileRackList: React.FC<Props> = ({ flatCells, onCellClick }) => {
  const { t } = useTranslation('workOrderDetails');
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const handleCellClick = useCallback(
    (cell: RackCell) => () => {
      onCellClick(cell);
    },
    [onCellClick]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {flatCells.map(cell => {
        const isEmpty = cell.cellState === 'empty';
        const colors = CELL_STATE_COLORS[cell.cellState];
        const borderColor = isDarkMode
          ? colors.border.dark
          : colors.border.light;

        return (
          <Box
            key={cell.sequenceNumber}
            onClick={handleCellClick(cell)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              borderLeft: `4px solid ${borderColor}`,
              borderRadius: '4px',
              backgroundColor: isEmpty
                ? isDarkMode
                  ? 'rgba(255,255,255,0.02)'
                  : 'rgba(0,0,0,0.02)'
                : 'background.paper',
              cursor: 'pointer',
              transition: 'background-color 0.15s ease',
              '&:hover': {
                backgroundColor: 'action.hover'
              },
              ...(isEmpty && {
                py: 1,
                opacity: 0.6
              })
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                fontFamily: 'monospace',
                minWidth: 56,
                color: isEmpty ? 'text.disabled' : 'text.primary'
              }}
            >
              {formatPositionLabel(cell.sequenceNumber)}
            </Typography>

            {isEmpty ? (
              <Typography variant="body2" color="text.disabled">
                {t('rack.noEquipment')}
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flex: 1,
                  minWidth: 0
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {cell.detail?.id || t('equipment.noAssetId')}
                </Typography>

                {cell.detail?.companyName && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {cell.detail.companyName}
                  </Typography>
                )}

                {cell.detail?.status && (
                  <StatusChip
                    label={cell.detail.status}
                    variant="status"
                    size="small"
                    sx={{ ml: 'auto', flexShrink: 0 }}
                  />
                )}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default MobileRackList;
